import apiService from '../api'

export interface GmailStatus {
  connected: boolean
  email?: string
  updatedAt?: string
  stockImportEnabled?: boolean
  oauthConfigured?: boolean
  stockImportConfigured?: boolean
  setupIssues?: string[]
  /** Backend OAuth callback — must match a Google Cloud Console authorized redirect URI. */
  oauthRedirectUri?: string
}

export interface StockReconImportResult {
  imported: boolean
  skipped: boolean
  messageId?: string
  subject?: string
  importRunId?: string
  reason?: string
  error?: string
}

export interface StockReconImportResponse {
  result: StockReconImportResult
  message: string
}

export async function getGmailStatus(): Promise<GmailStatus> {
  const res = await apiService.get<{ success: boolean; data: GmailStatus }>('/gmail/status')
  return (res as any)?.data ?? res
}

export async function getGmailAuthUrl(): Promise<string> {
  const res = await apiService.get<{ success: boolean; data: { authUrl: string } }>('/gmail/auth-url')
  return (res as any)?.data?.authUrl ?? ''
}

export async function disconnectGmail(): Promise<{ disconnected: boolean }> {
  const res = await apiService.post<{ success: boolean; data: { disconnected: boolean } }>('/gmail/disconnect')
  return (res as any)?.data ?? { disconnected: true }
}

export interface StockImportJobStatus {
  status: 'idle' | 'running' | 'success' | 'error'
  startedAt?: string
  finishedAt?: string
  result?: StockReconImportResult
  message?: string
}

export async function startStockImportFromGmail(): Promise<StockImportJobStatus> {
  const res = await apiService.post<{
    success: boolean
    data: StockImportJobStatus
    message?: string
  }>('/gmail/stock-import/run')
  return (res as any)?.data ?? res
}

export async function getStockImportJobStatus(): Promise<StockImportJobStatus> {
  const res = await apiService.get<{
    success: boolean
    data: StockImportJobStatus
  }>('/gmail/stock-import/status')
  return (res as any)?.data ?? res
}

export async function runStockImportFromGmail(): Promise<StockReconImportResponse> {
  await startStockImportFromGmail()

  const started = Date.now()
  const maxWaitMs = 5 * 60 * 1000

  while (Date.now() - started < maxWaitMs) {
    await new Promise((resolve) => setTimeout(resolve, 1500))
    const job = await getStockImportJobStatus()
    if (job.status === 'success' || job.status === 'error') {
      return {
        result: job.result ?? {
          imported: false,
          skipped: false,
          error: job.message,
        },
        message: job.message ?? '',
      }
    }
  }

  throw new Error('Stock import is still running. Refresh in a moment to see the updated stock levels.')
}
